/**
 * DashboardPage — the team-wide landing page (TRA-868).
 *
 * Same editorial band dialect as the model home, one level up: the team's
 * training curve, team-wide uptime, a stat row (requests, error rate,
 * tokens, p95, spend) over the team-wide request matrix, then the models
 * that are serving. Evals, checkpoints and deployment config are per-model
 * or per-group concerns and stay on their own pages.
 *
 * Training history is the checkpoint lineage of the team's first model —
 * real eval scores, one point per checkpoint. The team has one trained
 * model today, so "the team's curve" and "that model's curve" are the same
 * line; a team with several would need a picker here rather than a guess.
 * The band hides when no comparable checkpoints exist.
 *
 * Everything but the training curve comes from `/api/dashboard/overview`,
 * which reads the gateway with NO model filter — an unfiltered timeseries
 * IS the team aggregate, so the headline and the models list agree by
 * construction rather than by re-adding numbers here.
 */

import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { ModelsTable } from '@/components/models/ModelsTable'
import { ModelHomeSection } from '@/components/models/home/ModelHomeSection'
import { RequestsPanel } from '@/components/models/home/RequestsPanel'
import { RequestsTab } from '@/components/models/RequestsTab'
import { TrainingHistoryPanel } from '@/components/models/home/TrainingHistoryPanel'
import { UptimeGrid, UptimeLegend } from '@/components/models/home/UptimeGrid'
import type { RequestWindowKey } from '@/components/models/home/contracts'
import {
  initialLogView,
  type RequestLogView,
  withLogView,
} from '@/components/models/home/requestLogOptions'
import {
  lineageSubtitle,
  staleNotice,
  toRequestWindow,
  toTeamStats,
  toUptimeGrid,
  uptimeMonthLabels,
  uptimeSummary,
  widenWindow,
} from '@/components/models/home/contracts'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { PageContainer } from '@/components/ui/page-container'
import { LoadingPanel } from '@/components/shared/LoadingScreen'
import { useCheckpoints } from '@/hooks/useCheckpoints'
import { useMinimumLoading } from '@/hooks/useMinimumSplash'
import { useDashboardOverview, useTeamUptime } from '@/hooks/useUsage'
import { useTeam } from '@/contexts/TeamContext'

const WINDOW_LABELS: Record<RequestWindowKey, string> = {
  today: 'Today',
  week: '7-day',
  month: '30-day',
}

// The heading names the range the stat row and spend actually cover, so it
// must track the window toggle — a fixed "last 30 days" mislabels two of
// the three views.
const WINDOW_SUBTITLES: Record<RequestWindowKey, string> = {
  today: 'today',
  week: 'last 7 days',
  month: 'last 30 days',
}

export default function DashboardPage(): React.JSX.Element {
  // The window starts in AUTO: probe today, then widen to 7-day and 30-day
  // until one has traffic — a quiet morning shouldn't greet the reader
  // with an empty band when last month has the story. All three empty →
  // settle on the 7-day view with its empty grid. A manual toggle ends
  // auto for the visit.
  const [userWindow, setUserWindow] = useState<RequestWindowKey | null>(null)
  // Multi-turn runs are the level people reason at, so conversations come
  // first; `initialLogView` says why a deep link opens on the request log.
  const [searchParams, setSearchParams] = useSearchParams()
  const logView = initialLogView(searchParams)

  function setLogView(next: RequestLogView): void {
    setSearchParams((prev) => withLogView(prev, next), { replace: true })
  }

  function drillIntoFallbacks(): void {
    setSearchParams(
      (prev) => {
        const params = withLogView(prev, 'requests')
        params.set('fallback', '1')
        return params
      },
      { replace: true },
    )
    document.getElementById('request-log')?.scrollIntoView({ behavior: 'smooth' })
  }
  const [probe, setProbe] = useState<RequestWindowKey>('today')
  const [probesExhausted, setProbesExhausted] = useState(false)
  const window = userWindow ?? probe
  const { activeTeam } = useTeam()
  const overview = useDashboardOverview(window)

  const widening =
    userWindow === null &&
    !probesExhausted &&
    overview.data !== undefined &&
    overview.data.usage.request_count === 0

  // State adjusted during render (the you-might-not-need-an-effect
  // pattern): the re-render with the wider probe happens before anything
  // paints, so no intermediate "no requests" frame exists. Terminates
  // because the new probe's query starts as loading, which turns
  // `widening` off until its data lands.
  if (widening) {
    const next = widenWindow(probe)
    if (next.exhausted) setProbesExhausted(true)
    setProbe(next.probe)
  }
  const uptimeQuery = useTeamUptime()
  const checkpoints = useCheckpoints(overview.data?.models[0]?.id)

  const stats = useMemo(
    () =>
      overview.data
        ? toTeamStats(overview.data.usage, overview.data.timeseries)
        : [],
    [overview.data],
  )
  const requests = useMemo(
    () =>
      overview.data
        ? toRequestWindow(WINDOW_LABELS[window], overview.data.timeseries)
        : { label: WINDOW_LABELS[window], total: '0', buckets: [] },
    [overview.data, window],
  )
  const uptime = uptimeQuery.data
  // Every band, not just the first one. Releasing on `overview` alone lets
  // the page paint and then fill in — the uptime wall and the training
  // curve arrive later and each one shifts the layout under the reader.
  // Checkpoints only start once overview names a model, so this stays true
  // across that handoff. `useMinimumLoading` covers the gap between the two
  // fetches, where nothing is in flight but the page is not ready either.
  // `widening` counts as loading: each intermediate empty window would
  // otherwise paint a frame of "no requests" before the next probe lands.
  const loading = useMinimumLoading(
    overview.isLoading || widening || uptimeQuery.isLoading || checkpoints.isLoading,
  )

  // The hairline under the heading is a BAND separator — it works because
  // a band follows it. With one empty state below instead, it reads as a
  // rule under nothing, which is what makes the empty page look broken
  // rather than quiet.
  const header = (bands: boolean): React.JSX.Element => (
    <div className={`pb-6 ${bands ? 'border-b border-border' : ''}`}>
      <h1 className="font-serif text-2xl font-medium">Dashboard</h1>
      {/* Body sans, like every other page description (Evals, Models,
          Traces). Mono-uppercase is the MACHINE role — stat labels, table
          headers, slugs — and a sentence about what you're looking at is
          not machine text; setting it that way made this one page speak a
          different dialect from the rest of the app. */}
      <p className="mt-1 text-sm text-muted-foreground">
        {activeTeam?.name ?? 'Your team'} · {WINDOW_SUBTITLES[window]}
      </p>
    </div>
  )

  // One gate before anything renders. Skeleton bands followed by an empty
  // state tell the reader they have a fleet and then take it away — the
  // first frame should never claim something the second frame retracts.
  if (loading) {
    return (
      <PageContainer>
        {header(false)}
        <LoadingPanel />
      </PageContainer>
    )
  }

  // A failed overview is not an empty one. Without this branch the error
  // falls through to "Nothing to report yet", which tells a team with a
  // live fleet that they have never deployed anything — the most alarming
  // possible way to report a 500.
  if (overview.isError) {
    return (
      <PageContainer>
        {header(false)}
        <div className="py-16">
          <EmptyState
            title="Couldn't load your dashboard"
            description="The gateway usage overview didn't come back. This says nothing about your models. Reload the page to try again."
          />
        </div>
      </PageContainer>
    )
  }

  // A team with no models has nothing for any of these bands to be about.
  // Rendering them anyway produces a page of zeroes that reads as "your
  // model is down and serving nothing" rather than "you haven't started" —
  // 0ms latency, 0 requests, and an uptime wall with no cells in it. One
  // empty state says the true thing and points at the next step.
  //
  // Gated on a SUCCESSFUL response, not merely a finished one.
  //
  // Public-model traffic counts as something to report — a team calling only
  // `fireworks/glm-5.2` has no Model rows at all, and telling them they have
  // never deployed anything is the exact confusion this page now fixes.
  //
  // `public_models` alone is not enough to decide that: it is pinned to 24h
  // while the window probe widens to 7- and 30-day. Public traffic that
  // stopped 25 hours ago leaves a nonzero headline and an empty public band,
  // which would send a team WITH traffic to "you haven't started". The
  // selected window's request count is the honest test — if this window has
  // requests, the page has something to say.
  if (
    overview.data !== undefined &&
    overview.data.models.length === 0 &&
    overview.data.public_models.length === 0 &&
    overview.data.usage.request_count === 0
  ) {
    return (
      <PageContainer>
        {header(false)}
        <div className="py-16">
          <EmptyState
            title="Nothing to report yet"
            description="Once we've trained and deployed a model for your team, its training history, availability and gateway traffic all show up here."
            action={
              <Button asChild>
                <Link to="/models">Go to models</Link>
              </Button>
            }
          />
        </div>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      {header(true)}

      {/* Past the loading gate every query has settled, so a band is
          either shown with its data or not shown at all — no in-band
          skeletons left to flash. */}
      {checkpoints.data && checkpoints.data.checkpoints.length > 0 ? (
        <ModelHomeSection
          title="Training history"
          subtitle={lineageSubtitle(checkpoints.data)}
        >
          <TrainingHistoryPanel checkpoints={checkpoints.data} />
        </ModelHomeSection>
      ) : null}

      <ModelHomeSection
        title="Uptime"
        subtitle={uptime ? uptimeSummary(uptime) : 'Last 90 days in 8-hour blocks'}
        action={<UptimeLegend />}
      >
        {uptimeQuery.isError ? (
          <p className="text-sm text-destructive">Failed to load availability.</p>
        ) : uptime ? (
          <UptimeGrid
            days={toUptimeGrid(uptime)}
            months={uptimeMonthLabels(uptime)}
            onFallbackClick={drillIntoFallbacks}
          />
        ) : (
          <p className="text-sm text-muted-foreground">No availability recorded yet</p>
        )}
      </ModelHomeSection>

      <ModelHomeSection
        title="Requests"
        subtitle="Every request through the inference gateway, all models · deltas vs the preceding window"
      >
        {/* The api answered from its memory because the gateway failed
            (TRA-1531). Say how old the numbers are; nothing else changes. */}
        {overview.data?.stale_as_of ? (
          <p className="mb-3 text-xs text-muted-foreground">
            {staleNotice(overview.data.stale_as_of, new Date())}
          </p>
        ) : null}
        <RequestsPanel
          stats={stats}
          data={requests}
          window={window}
          onWindowChange={setUserWindow}
          emptyGrid={probesExhausted}
        />
      </ModelHomeSection>

      <ModelHomeSection
        title="Models"
        subtitle="Everything this team calls through the gateway · last 24h"
        action={
          <Button variant="outline" size="sm" asChild>
            <Link to="/models">All models</Link>
          </Button>
        }
      >
        {/* The page is now reachable with zero aliases (public traffic only),
            where ModelsTable renders a header row over nothing. Say why it's
            empty instead — and the emptiness is the POINT for a team whose
            traffic all went to the catalog. */}
        {overview.data &&
        (overview.data.models.length > 0 ||
          overview.data.public_models.length > 0) ? (
          <>
            {/* healthLoaded=false renders dashes in the status and count
                columns — reused here when the gateway's volume read failed,
                because the zeros in `models` are fabricated, not data. */}
            <ModelsTable
              models={overview.data.models}
              publicModels={overview.data.public_models}
              healthLoaded={!overview.data.per_model_unavailable}
            />
            {overview.data.per_model_unavailable && (
              <p className="mt-2 text-xs text-muted-foreground">
                Per-model traffic is temporarily unavailable. The gateway
                read failed. The totals above are unaffected.
              </p>
            )}
          </>
        ) : overview.data?.per_model_unavailable ? (
          // A failed volume read also empties the public-models list, so a
          // catalog-only team would otherwise be told they never called
          // anything — the exact confusion TRA-886 fixed.
          <p className="text-sm text-muted-foreground">
            Per-model traffic is temporarily unavailable. The gateway read
            failed.
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">
            Nothing called through the gateway yet.
          </p>
        )}
      </ModelHomeSection>

      {/* ONE traffic band (TRA-901): the heading follows the toggle, so the
          band is "Conversations" or "Request log" — never both stacked.
          The id is the uptime wall's fallback drill-down scroll target. */}
      <div id="request-log">
        <ModelHomeSection
          title={logView === 'requests' ? 'Request log' : 'Conversations'}
          subtitle={
            logView === 'requests'
              ? 'Every request through the gateway'
              : 'Multi-turn agent runs through the gateway, most recently active first'
          }
        >
          <RequestsTab
            view={logView}
            onView={setLogView}
            pageName="dashboard"
            models={overview.data?.models ?? []}
            gatewayTeamSlug={overview.data?.gateway_team_slug ?? null}
          />
        </ModelHomeSection>
      </div>

    </PageContainer>
  )
}
